import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Phone, MapPin } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  usePageTitle('Contact Us');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferred_type: '',
    preferred_location: '',
    budget_range: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/inquiries`, formData);
      toast.success('Thank you! We\'ll be in touch shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        preferred_type: '',
        preferred_location: '',
        budget_range: '',
      });
    } catch (error) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Contact Info */}
        <div>
          <h1
            className="text-5xl md:text-6xl font-medium tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="contact-title"
          >
            Let's <span className="text-gold">Connect</span>
          </h1>
          <p className="text-lg text-white/70 leading-relaxed mb-12">
            Whether you're exploring charter options, seeking purchase advice, or have questions about Southeast Asian cruising, our team is here to assist.
          </p>

          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="glass-light p-4">
                <Mail className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Email</h3>
                <a href="mailto:info@seluxuryyachts.com" className="text-white/70 hover:text-gold transition-colors" data-testid="contact-email">
                  info@seluxuryyachts.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="glass-light p-4">
                <Phone className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">WhatsApp</h3>
                <a
                  href="https://wa.me/1234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-gold transition-colors"
                  data-testid="contact-whatsapp"
                >
                  +1 (234) 567-890
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="glass-light p-4">
                <MapPin className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Regional Offices</h3>
                <p className="text-white/70">
                  Singapore • Phuket • Bali • Manila
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="glass-light p-10">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <Label htmlFor="name" className="text-white/80">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                required
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white/80">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                required
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-white/80">Phone / WhatsApp</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                data-testid="contact-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="preferred_type" className="text-white/80">Yacht Type Interest</Label>
              <Input
                id="preferred_type"
                placeholder="e.g., Superyacht, Catamaran"
                value={formData.preferred_type}
                onChange={(e) => setFormData({ ...formData, preferred_type: e.target.value })}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                data-testid="contact-type-input"
              />
            </div>

            <div>
              <Label htmlFor="preferred_location" className="text-white/80">Preferred Location</Label>
              <Input
                id="preferred_location"
                placeholder="e.g., Thailand, Indonesia"
                value={formData.preferred_location}
                onChange={(e) => setFormData({ ...formData, preferred_location: e.target.value })}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                data-testid="contact-location-input"
              />
            </div>

            <div>
              <Label htmlFor="budget_range" className="text-white/80">Budget Range</Label>
              <Input
                id="budget_range"
                placeholder="e.g., $50k-100k/week"
                value={formData.budget_range}
                onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                data-testid="contact-budget-input"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-white/80">Message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-transparent border border-white/20 rounded-none focus:border-gold mt-2 min-h-[120px]"
                required
                data-testid="contact-message-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D] py-6 uppercase tracking-widest rounded-none"
              disabled={isLoading}
              data-testid="contact-submit-btn"
            >
              {isLoading ? 'Sending...' : 'Send Inquiry'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
